import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { isMenuRouteAllowed, firstGrantedPath } from '@/lib/menuPermissions';
import { SidebarProvider, useSidebar } from '@/components/ui/sidebar';
import { AppSidebar } from './AppSidebar';
import { TopNavigation } from './TopNavigation';
import { CardShell } from './cardShell/CardShell';
import { isCardShellEnabled } from './cardShell/shellVariant';
import { useIsMobile } from '@/hooks/use-mobile';
import BillingDialog from './FreeTrailSteps/BillingDialog';
import { useCurrentSubscription } from '@/hooks/useCurrentSubscription';
import { TrialBanner } from '../trial/TrialBanner';
import { WabaLockedBanner } from '../WabaLockedBanner';
import { CoexistenceStateBanner } from '../CoexistenceStateBanner';
import { AccountReviewBanner } from '../AccountReviewBanner';
import { WabaRestrictionsBanner } from '../WabaRestrictionsBanner';
import { buildCoexistenceNotice } from '@/lib/whatsapp/coexistenceState';
import { HistorySyncBanner } from '../HistorySyncBanner';
import {
  buildHistorySyncNotice,
  historySyncNeedsClock,
} from '@/lib/whatsapp/historySync';
import { useIsSyncingChatHistory, useSyncChatHistory } from '@/hooks/useBusinessAccounts';
import { hasAccountRestrictions } from '@/lib/whatsapp/accountRestrictions';
import { isReviewBlocked } from '@/lib/whatsapp/accountReview';
import { MetaTokenExpiryBanner } from '../MetaTokenExpiryBanner';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
import { AccountScopeBoundary } from './AccountScopeBoundary';
import { useAdsSetupStatus } from '@/hooks/useAds';

import { useAuth } from '@/components/AuthProvider';
import { useInboxSoundNotification } from '@/hooks/useInboxSoundNotification';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import { useSidebarTour } from '@/hooks/useSidebarTour';
import { OnboardingChecklist } from '../Onboarding/OnboardingChecklist';
import { SubscriptionGuardProvider } from '@/contexts/SubscriptionGuardContext';
import { AiChatProvider } from '@/contexts/AiChatContext';
import { ChatWidget } from './ChatWidget/ChatWidget';
import { GenUIProvider, useGenUI, GenUIChatbox } from '@/components/genui';
import { GenUIProvider as GenUIFullProvider } from '@/contexts/GenUIContext';
import { PushNotificationManager } from '../PushNotificationManager';
import { PWAInstallPrompt } from '../PWAInstallPrompt';
import { WhatsNewModal } from '../WhatsNewModal';
import { WorkspaceTourHost } from './WorkspaceTourHost';
import { AdminAnnouncementModal } from '../notifications/AdminAnnouncementModal';
import { InAppAnnouncementHost } from '../notifications/InAppAnnouncementHost';
import { PortalCampaignHost } from '../campaigns/PortalCampaignHost';
import { PortalCampaignBanner } from '../campaigns/PortalCampaignBanner';
import { isSandboxMode } from '@/sandbox/env';
import { TourGuide } from '@/tour-guide';
import { setBlockingDialog } from '@/lib/blockingDialogs';
import {
  ONBOARDING_V2_PREFLIGHT_PATH,
} from '@/components/onboarding-v2/onboardingV2Routes';
import { cn } from "@/lib/utils";
import { useMainScrollRestore } from '@/hooks/useMainScrollRestore';
import { isTrialActuallyExpired as checkTrialExpired, isSubscriptionActive, isSubscriptionExpiredOrCancelled } from "@/lib/subscription-utils";
import { Button } from '@/components/ui/button';

const IS_SANDBOX = isSandboxMode();

const SOCIAL_FLOW_BUILDER_RE =
  /^\/home\/automation\/(facebook|instagram)\/(new|[^/]+)$/;

function isSocialFlowBuilderPath(pathname: string): boolean {
  return SOCIAL_FLOW_BUILDER_RE.test(pathname);
}

interface AppShellProps {
  children: React.ReactNode;
}

const HISTORY_SYNC_TICK_MS = 60 * 1000;

const AppShellContent: React.FC<AppShellProps> = ({ children }) => {
  const { state, setOpen } = useSidebar();
  const location = useLocation();
  const cardShellEnabled = React.useMemo(
    () => isCardShellEnabled(location.search),
    [location.search]
  );
  const mainScrollRef = useMainScrollRestore(location.pathname, location.search);
  const isMobile = useIsMobile();
  const initializedPathnameRef = React.useRef<string | null>(null);
  const [showBillingDialog, setShowBillingDialog] = React.useState(false);
  const { user, loading: authLoading } = useAuth();
  const { isOpen: isGenUIOpen } = useGenUI();
  const isGenUIMode = isGenUIOpen;

  const prevGenUIMode = React.useRef(isGenUIMode);
  const prevSidebarState = React.useRef<string>(state);
  React.useEffect(() => {
    if (isGenUIMode && !prevGenUIMode.current) {
      prevSidebarState.current = state;
      setOpen(false);
    } else if (!isGenUIMode && prevGenUIMode.current) {
      if (prevSidebarState.current === 'expanded') {
        setOpen(true);
      }
    }
    prevGenUIMode.current = isGenUIMode;
  }, [isGenUIMode]);

  const roleRaw = (user?.role || '').toString().toUpperCase();
  const isSuperAdmin = !!(user as any)?.is_super_admin;
  const isAgency = !!(user as any)?.is_agency;
  const isRegularUser = !isSuperAdmin && !isAgency;

  const previousPathnameRef = React.useRef<string>(location.pathname);

  const organizationId = user?.organization_id;

  const { data: currentSubscription, isLoading: isSubscriptionLoading, isError: isSubscriptionError } = useCurrentSubscription({
    enabled: !!organizationId && !authLoading && isRegularUser,
    staleTime: 5 * 60 * 1000, // AppShell is the outermost owner — keep its longer 5m TTL.
  });

  const hasAutoOpenedBillingRef = React.useRef(false);

  React.useEffect(() => {
    if (!isRegularUser || typeof window === 'undefined' || !organizationId || authLoading) return;

    if (currentSubscription !== undefined) {
      if (isSubscriptionActive(currentSubscription)) {
        setShowBillingDialog(false);
        localStorage.removeItem('showBillingDialog');
        hasAutoOpenedBillingRef.current = false;
        return;
      }

      const isMainPage = location.pathname.startsWith('/home') &&
        !location.pathname.startsWith('/home/admin') &&
        !location.pathname.startsWith('/home/agency');

      const isExpiredOrCancelled = isSubscriptionExpiredOrCancelled(currentSubscription);

      if (isExpiredOrCancelled && isMainPage && !showBillingDialog && !hasAutoOpenedBillingRef.current) {
        hasAutoOpenedBillingRef.current = true;
        localStorage.removeItem('showBillingDialog');
        setShowBillingDialog(true);
        return;
      }

      const shouldShowFromStorage = localStorage.getItem('showBillingDialog');

      if (shouldShowFromStorage === 'true') {
        localStorage.removeItem('showBillingDialog');
        if (isMainPage && !showBillingDialog) {
          setShowBillingDialog(true);
        }
      }
    }
  }, [location.pathname, currentSubscription, organizationId, showBillingDialog, authLoading, isRegularUser]);

  const secondarySidebarWidth = 0;
  const currentSidebarWidth = state === 'expanded' ? 'var(--sidebar-width)' : 'var(--sidebar-width-icon)';
  const marginLeft = isMobile ? 0 : currentSidebarWidth;
  const width = isMobile ? '100%' : `calc(100% - ${currentSidebarWidth})`;

  const isAdminRoute = location.pathname.startsWith('/admin');
  const isAgencyRoute = location.pathname.startsWith('/home/agency');
  const isAdminDashboard = location.pathname === '/admin/dashboard';
  const isAgencyDashboard = location.pathname === '/home/agency/dashboard';
  const isRegularDashboard = location.pathname === '/home';
  const isNewInbox = location.pathname === '/home/new-inbox';
  const isGenUIWorkspace = location.pathname === '/home/genui';
  const isDashboardV2 = location.pathname === '/home/V3/dashboard' || location.pathname === '/home';
  const isWebsiteBuilderEditor = /^\/home\/website-builder\/sites\/[^/]+\/editor\/[^/]+$/.test(location.pathname);

  const mainScrollbarClass = isDashboardV2 ? 'scrollbar-reveal-root' : 'scrollbar-hide';

  const removeTopPadding = isAdminDashboard || isAgencyDashboard || isRegularDashboard;

  const isAdminLibraryBuilder = location.pathname === '/admin/library/flow/builder' ||
    location.pathname === '/admin/library/whatsapp-flow/builder';

  const isFlowBuilderPage = isAdminLibraryBuilder || (isRegularUser && (
    (location.pathname.startsWith('/home/automation/flows/') &&
      (location.pathname.includes('/new') || location.pathname.match(/\/home\/automation\/flows\/[^/]+$/))) ||
    isSocialFlowBuilderPath(location.pathname)
  ));
  const isCompactGenUIPage = isFlowBuilderPage || location.pathname === '/home/flows/builder';

  const isFullscreenBuilder = isFlowBuilderPage && !isAdminLibraryBuilder;

  const isKanbanBoardPage = isRegularUser && (
    (location.pathname.startsWith('/home/boards/') && location.pathname.split('/').length > 3)
    || location.pathname === '/home/deals'
    || location.pathname === '/home/tickets'
  );

  const isSocialPostsWorkspace = isRegularUser && (
    location.pathname === '/home/social-media/posts'
    || location.pathname === '/home/social-media/posts/create-global'
  );

  const isChannelsInbox = isRegularUser && location.pathname.startsWith('/home/channels/');

  const isAuditLogWorkspace = isRegularUser && location.pathname === '/home/audit-log';

  const isCommerceV2Workspace =
    isRegularUser && location.pathname === '/home/commerce/v2';

  const isPetpoojaWorkspace =
    isRegularUser && location.pathname === '/home/petpooja';

  const isFixedChromeListWorkspace = isRegularUser && (
    location.pathname === '/home/automation'
    || location.pathname === '/home/automation/flows'
    || location.pathname === '/home/new-campaigns'
    || location.pathname === '/home/new-templates'
  );

  const isFullBleedWorkspace =
    isKanbanBoardPage ||
    isSocialPostsWorkspace ||
    isChannelsInbox ||
    isAuditLogWorkspace ||
    isCommerceV2Workspace ||
    isPetpoojaWorkspace ||
    isFixedChromeListWorkspace;

  const isAutomationsCardThemeScroll = isRegularUser && (
    location.pathname === '/home/automation'
    || location.pathname === '/home/automation/flows'
    || location.pathname === '/home/flows'
    || location.pathname === '/home/team-management'
    || location.pathname === '/home/ai-agents'
    || /\/home\/automation\/flows\/[^/]+\/executions$/.test(location.pathname)
    || /\/home\/automation\/flows\/[^/]+\/analytics$/.test(location.pathname)
    || /\/home\/automation\/facebook\/[^/]+\/analytics$/.test(location.pathname)
    || location.pathname === '/home/new-campaigns'
    || location.pathname === '/home/new-templates'
  );

  const isDataStoreCardThemeScroll =
    isRegularUser && /^\/home\/data-stores(\/[^/]+)?$/.test(location.pathname);

  const isIntegrationsCardThemeScroll =
    isRegularUser && location.pathname === '/home/integrations';

  const isAccountSettingsWorkspace =
    isRegularUser && location.pathname === '/home/account-settings';

  const isWorkspaceSettingsWorkspace =
    isRegularUser && location.pathname === '/home/workspace-settings';

  const isAppointmentsV2Workspace =
    isRegularUser && location.pathname === '/home/appointments-v2';

  const isCardThemeScroll =
    isAutomationsCardThemeScroll
    || isDataStoreCardThemeScroll
    || isIntegrationsCardThemeScroll
    || isAccountSettingsWorkspace
    || isWorkspaceSettingsWorkspace
    || isAppointmentsV2Workspace;

  const useMinHeight = (isAdminRoute && !isAdminLibraryBuilder) || isAgencyRoute || isNewInbox;

  useInboxSoundNotification();

  const { totalUnreadConversations } = useUnreadMessages();
  React.useEffect(() => {
    const baseTitle = 'WeNext - Enterprise WhatsApp Business Platform';
    document.title = totalUnreadConversations > 0
      ? `(${totalUnreadConversations}) ${baseTitle}`
      : baseTitle;
  }, [totalUnreadConversations]);

  const hasActivePlan = isSubscriptionActive(currentSubscription);

  const hasTrialDates = currentSubscription?.trial_end && currentSubscription?.trial_start;
  const isTrialing = currentSubscription?.status === 'trialing' && hasTrialDates && !checkTrialExpired(currentSubscription);
  const isTrialExpired = (currentSubscription?.status === 'expired' && hasTrialDates) || checkTrialExpired(currentSubscription);

  const isPaidExpired = currentSubscription?.status === 'expired' && !hasTrialDates;
  const isPaidExpiring = currentSubscription?.status === 'active' &&
    currentSubscription?.cancel_at_period_end &&
    currentSubscription?.current_period_end;

  const isTrialPlan = !!(isTrialing || isTrialExpired);

  const expiryDate = isTrialing && currentSubscription?.trial_end
    ? new Date(currentSubscription.trial_end)
    : isPaidExpiring && currentSubscription?.current_period_end
      ? new Date(currentSubscription.current_period_end)
      : null;
  const daysRemaining = expiryDate
    ? Math.max(0, Math.ceil((expiryDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;

  const isSubscriptionExpired = isTrialExpired || isPaidExpired;

  const isPendingOrPastDue = currentSubscription &&
    (currentSubscription.status === 'pending' || currentSubscription.status === 'past_due');

  const hasNoPlan = !hasActivePlan && !isSubscriptionExpired && !isPendingOrPastDue;

  const showSubscriptionBanner = isRegularUser && !isFullscreenBuilder && !authLoading && !isSubscriptionLoading && !isSubscriptionError && (
    (isTrialing && daysRemaining <= 5) ||
    (isPaidExpiring && daysRemaining <= 5) ||
    isSubscriptionExpired ||
    hasNoPlan
  );

  const [bannerDismissed, setBannerDismissed] = React.useState(false);

  const { selectedAccount } = useBusinessAccountContext();
  const isWabaLocked =
    !!selectedAccount && selectedAccount.lock_status === 'LOCKED';
  const [wabaBannerDismissed, setWabaBannerDismissed] = React.useState(false);
  const isOnboardingV2 = location.pathname.startsWith(ONBOARDING_V2_PREFLIGHT_PATH);
  const showWabaLockedBanner =
    isRegularUser &&
    !isFullscreenBuilder &&
    isWabaLocked &&
    !wabaBannerDismissed &&
    !isOnboardingV2;

  const coexistenceNotice = buildCoexistenceNotice({
    state: selectedAccount?.coexistence_state,
    reason: selectedAccount?.coexistence_reason,
    accountName: selectedAccount?.name,
    phoneNumber: selectedAccount?.phone_number,
  });
  const [coexistenceBannerDismissed, setCoexistenceBannerDismissed] =
    React.useState(false);
  const showCoexistenceBanner =
    isRegularUser &&
    !isFullscreenBuilder &&
    !!coexistenceNotice &&
    !coexistenceBannerDismissed &&
    !isOnboardingV2;

  const [reviewBannerDismissed, setReviewBannerDismissed] = React.useState(false);
  const showAccountReviewBanner =
    isRegularUser &&
    !isFullscreenBuilder &&
    isReviewBlocked(selectedAccount?.review_decision) &&
    !reviewBannerDismissed &&
    !isOnboardingV2;

  const [, setHistorySyncTick] = React.useState(0);
  const historySyncNow = new Date();
  const historySyncClockRunning = historySyncNeedsClock(
    selectedAccount ?? null,
    historySyncNow
  );
  React.useEffect(() => {
    if (!historySyncClockRunning) return;
    const id = window.setInterval(
      () => setHistorySyncTick((t) => t + 1),
      HISTORY_SYNC_TICK_MS
    );
    return () => window.clearInterval(id);
  }, [historySyncClockRunning]);

  const historySyncNotice = buildHistorySyncNotice(
    selectedAccount ?? null,
    historySyncNow
  );
  const [historySyncBannerDismissed, setHistorySyncBannerDismissed] =
    React.useState(false);
  const syncChatHistory = useSyncChatHistory();
  const syncingChatHistoryAnywhere = useIsSyncingChatHistory();
  const isUnknownWindowOffer =
    !!historySyncNotice &&
    !historySyncNotice.window.known &&
    historySyncNotice.showSync &&
    historySyncNotice.tone === 'info';
  const showHistorySyncBanner =
    isRegularUser &&
    !isFullscreenBuilder &&
    !!historySyncNotice &&
    !isUnknownWindowOffer &&
    !historySyncBannerDismissed &&
    !isOnboardingV2;

  const isRestricted = hasAccountRestrictions(selectedAccount?.restrictions);
  const [restrictionsBannerDismissed, setRestrictionsBannerDismissed] =
    React.useState(false);
  const showRestrictionsBanner =
    isRegularUser &&
    !isFullscreenBuilder &&
    isRestricted &&
    !restrictionsBannerDismissed &&
    !isOnboardingV2;

  const { data: adsSetupStatus } = useAdsSetupStatus();
  const adsAccount = adsSetupStatus?.account;
  const adsTokenStatus = adsAccount?.token_status;
  const isAdsTokenAttention =
    adsTokenStatus === 'EXPIRING_SOON' || adsTokenStatus === 'EXPIRED';
  const [adsBannerDismissed, setAdsBannerDismissed] = React.useState(false);
  const lastAdsStatusRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (adsTokenStatus && lastAdsStatusRef.current !== adsTokenStatus) {
      setAdsBannerDismissed(false);
      lastAdsStatusRef.current = adsTokenStatus;
    }
  }, [adsTokenStatus]);
  const showAdsTokenExpiryBanner =
    isRegularUser && !isFullscreenBuilder && isAdsTokenAttention && !adsBannerDismissed;

  const META_STRIP_HEIGHT_PX = 53;
  const COEXISTENCE_STRIP_HEIGHT_PX = 73;
  const RESTRICTIONS_STRIP_HEIGHT_PX = COEXISTENCE_STRIP_HEIGHT_PX;
  const HISTORY_SYNC_STRIP_HEIGHT_PX = COEXISTENCE_STRIP_HEIGHT_PX;
  const HISTORY_SYNC_STRIP_PROGRESS_HEIGHT_PX = HISTORY_SYNC_STRIP_HEIGHT_PX + 12;
  const metaStripsHeight =
    (showWabaLockedBanner ? META_STRIP_HEIGHT_PX : 0) +
    (showCoexistenceBanner ? COEXISTENCE_STRIP_HEIGHT_PX : 0) +
    (showRestrictionsBanner ? RESTRICTIONS_STRIP_HEIGHT_PX : 0) +
    (showAccountReviewBanner ? COEXISTENCE_STRIP_HEIGHT_PX : 0) +
    (showHistorySyncBanner
      ? historySyncNotice?.progress !== null && historySyncNotice?.progress !== undefined
        ? HISTORY_SYNC_STRIP_PROGRESS_HEIGHT_PX
        : HISTORY_SYNC_STRIP_HEIGHT_PX
      : 0) +
    (showAdsTokenExpiryBanner ? META_STRIP_HEIGHT_PX : 0);

  React.useEffect(() => {
    setBlockingDialog('billing', showBillingDialog);
    return () => setBlockingDialog('billing', false);
  }, [showBillingDialog]);

  const subscriptionBannerHeight = showSubscriptionBanner && !bannerDismissed ? 40 : 0;
  const bannerHeight = subscriptionBannerHeight;

  const chromeStackRef = React.useRef<HTMLDivElement | null>(null);
  const [chromeStackHeight, setChromeStackHeight] = React.useState<number | null>(null);

  React.useLayoutEffect(() => {
    const el = chromeStackRef.current;
    if (!el) {
      setChromeStackHeight((prev) => (prev === null ? prev : null));
      return;
    }
    const measure = () => {
      const next = Math.round(el.getBoundingClientRect().height);
      if (!Number.isFinite(next)) return;
      setChromeStackHeight((prev) => (prev === next ? prev : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [location.pathname]);

  const reservedChromeHeight = chromeStackHeight ?? metaStripsHeight;

  const { startTour } = useSidebarTour({
    enabled: !authLoading && !!user && isRegularUser && !!hasActivePlan,
    user,
    hasActivePlan: !!hasActivePlan,
    sidebarState: state,
    setSidebarOpen: setOpen,
  });
  const navigate = useNavigate();

  const menuPerms = (user as any)?.menu_permissions;
  const inHomeArea = location.pathname === '/home' || location.pathname.startsWith('/home/');
  if (
    isRegularUser &&
    Array.isArray(menuPerms) &&
    inHomeArea &&
    !isAgencyRoute &&
    !isMenuRouteAllowed(location.pathname, menuPerms)
  ) {
    const target = firstGrantedPath(menuPerms);
    if (target !== location.pathname) {
      return <Navigate to={target} replace />;
    }
  }


  const mainPaddingTop =
    (isWebsiteBuilderEditor
      ? 0
      : isFlowBuilderPage
        ? 0
        : isGenUIWorkspace
          ? (isMobile ? 20 : 60)
          : isFullBleedWorkspace || isCardThemeScroll
            ? (isMobile ? 20 : 60)
            : (!removeTopPadding
              ? (isMobile ? 80 : 100)
              : (isMobile ? 20 : 60))) + reservedChromeHeight;

  const metaChromeStack = (
    <>
      {showWabaLockedBanner && (
        <WabaLockedBanner
          accountName={selectedAccount?.name}
          phoneNumber={selectedAccount?.phone_number}
          onDismiss={() => setWabaBannerDismissed(true)}
        />
      )}
      {showCoexistenceBanner && (
        <CoexistenceStateBanner
          coexistenceState={selectedAccount?.coexistence_state}
          coexistenceReason={selectedAccount?.coexistence_reason}
          accountName={selectedAccount?.name}
          phoneNumber={selectedAccount?.phone_number}
          onDismiss={() => setCoexistenceBannerDismissed(true)}
        />
      )}
      {showAccountReviewBanner && (
        <AccountReviewBanner
          reviewDecision={selectedAccount?.review_decision}
          accountName={selectedAccount?.name}
          phoneNumber={selectedAccount?.phone_number}
          onDismiss={() => setReviewBannerDismissed(true)}
        />
      )}
      {showRestrictionsBanner && (
        <WabaRestrictionsBanner
          restrictions={selectedAccount?.restrictions}
          accountName={selectedAccount?.name}
          phoneNumber={selectedAccount?.phone_number}
          onDismiss={() => setRestrictionsBannerDismissed(true)}
        />
      )}
      {showHistorySyncBanner && (
        <HistorySyncBanner
          account={selectedAccount ?? null}
          now={historySyncNow}
          syncing={syncChatHistory.isPending}
          disabled={syncingChatHistoryAnywhere}
          onSync={() => {
            if (!selectedAccount?.id) return;
            syncChatHistory.mutate(selectedAccount.id);
          }}
          onDismiss={() => setHistorySyncBannerDismissed(true)}
        />
      )}
      {showAdsTokenExpiryBanner && (
        <MetaTokenExpiryBanner
          tokenStatus={adsTokenStatus as 'EXPIRING_SOON' | 'EXPIRED'}
          tokenExpiry={adsAccount?.token_expiry}
          adAccountName={adsAccount?.ad_account_name}
          onDismiss={() => setAdsBannerDismissed(true)}
        />
      )}
      <PortalCampaignBanner />
    </>
  );

  const globalOverlays = (
    <>
      {isRegularUser && (
        <BillingDialog open={showBillingDialog} onOpenChange={setShowBillingDialog} />
      )}

      <ChatWidget />

      {isCompactGenUIPage && <GenUIChatbox />}

      <PushNotificationManager />

      <PWAInstallPrompt />

      {isRegularUser && !isOnboardingV2 && <WhatsNewModal />}
      {isRegularUser && !IS_SANDBOX && <WorkspaceTourHost />}
    </>
  );

  if (cardShellEnabled && !isWebsiteBuilderEditor && !isFullscreenBuilder) {
    return (
      <>
        <CardShell
          banners={metaChromeStack}
          onStartTour={startTour}
          onOpenBilling={() => setShowBillingDialog(true)}
        >
          {children}
        </CardShell>

        <TourGuide
          enabled={IS_SANDBOX}
          persist={IS_SANDBOX}
          withDemoBooking={IS_SANDBOX}
          withPricing={IS_SANDBOX}
          withTasks={IS_SANDBOX}
          withLauncher
          withTracker
        />

        {showSubscriptionBanner && (
          <div className="fixed top-0 left-0 right-0 z-[70]">
            <TrialBanner
              daysRemaining={daysRemaining}
              isExpired={!!isSubscriptionExpired || !!hasNoPlan}
              isTrialPlan={isTrialPlan}
              hasNoPlan={!!hasNoPlan}
              onDismiss={() => setBannerDismissed(true)}
            />
          </div>
        )}

        {globalOverlays}
      </>
    );
  }

  return (
    <div className="w-full h-screen bg-background relative">
      {!isWebsiteBuilderEditor && !isFullscreenBuilder && (
        <div
          style={{
            marginTop: bannerHeight ? `${bannerHeight}px` : '0',
          }}
        >
          <AppSidebar />
        </div>
      )}

      {!isWebsiteBuilderEditor && !isFullscreenBuilder && (
        <div
          data-app-topbar=""
          className="fixed right-0 z-50 pointer-events-auto transition-[left,top] duration-200 ease-smooth"
          style={{
            left: marginLeft,
            top: bannerHeight
          }}
        >
          <TopNavigation onStartTour={startTour} onOpenBilling={() => setShowBillingDialog(true)} />
        </div>
      )}

      {!isWebsiteBuilderEditor && (
        <TourGuide
          enabled={IS_SANDBOX}
          persist={IS_SANDBOX}
          withDemoBooking={IS_SANDBOX && !isFullscreenBuilder}
          withPricing={IS_SANDBOX && !isFullscreenBuilder}
          withTasks={IS_SANDBOX}
          withLauncher={!isFullscreenBuilder}
          withTracker={!isFullscreenBuilder}
        />
      )}

      {!isWebsiteBuilderEditor && !isFullscreenBuilder && (
        <div
          ref={chromeStackRef}
          className="fixed right-0 z-40 pointer-events-auto transition-[left,top] duration-200 ease-smooth top-[calc(var(--app-banner-top,0px)+var(--app-header-height))]"
          style={{ left: marginLeft, ['--app-banner-top' as any]: `${bannerHeight}px` }}
        >
          {metaChromeStack}
        </div>
      )}

      <main
        id="app-main-scroll"
        ref={mainScrollRef}
        className={[
          'bg-gradient-subtle relative z-0 transition-[left,width] duration-200 ease-smooth',
          isWebsiteBuilderEditor
            ? 'h-screen overflow-hidden p-0 m-0'
            : isFlowBuilderPage
              ? 'h-screen overflow-hidden' // Full height, no padding for FlowBuilder page only - FlowBuilder handles its own padding
              : isGenUIWorkspace
                ? 'flex h-screen flex-col overflow-hidden'
                : isFullBleedWorkspace
                  ? 'flex h-screen flex-col overflow-hidden pt-[20px] sm:pt-[60px] mt-[40px] sm:mt-0'
                  : isCardThemeScroll
                    ? cn(
                      'h-screen overflow-y-auto overflow-x-hidden pt-[20px] sm:pt-[60px] mt-[40px] sm:mt-0',
                      mainScrollbarClass
                    )
                    : useMinHeight
                      ? !removeTopPadding
                        ? cn(
                          "min-h-screen pt-[80px] sm:pt-[100px] pb-4 sm:pb-6 lg:pb-8",
                          !isNewInbox && "px-4 sm:px-6 lg:px-8"
                        )
                        : 'min-h-screen pt-[20px] sm:pt-[60px]'
                      : !removeTopPadding
                        ? cn(
                          isDashboardV2 ? "pt-16 sm:pt-[72px]" : "pt-[80px] sm:pt-[100px]",
                          "h-screen overflow-y-auto overflow-x-hidden pb-4 sm:pb-6 lg:pb-8 mt-[40px] sm:mt-0",
                          mainScrollbarClass,
                          !isNewInbox && "px-4 sm:px-6 lg:px-8"
                        )
                        : cn(
                          'h-screen overflow-y-auto overflow-x-hidden pt-[20px] sm:pt-[60px] mt-[40px] sm:mt-0',
                          mainScrollbarClass
                        )
        ].join(' ')}
        style={{
          left: (isWebsiteBuilderEditor || isFullscreenBuilder) ? 0 : marginLeft,
          width: (isWebsiteBuilderEditor || isFullscreenBuilder) ? '100%' : width,
          position: 'absolute',
          top: 0, // Push content down by announcement height
          right: 0,
          ['--app-banner-offset' as any]: `${reservedChromeHeight}px`,
          paddingTop: mainPaddingTop,
          ['--app-content-top' as any]: `${mainPaddingTop}px`,
        }}
      >
        {(isFullBleedWorkspace || isGenUIWorkspace) ? (
          <div className="flex min-h-0 flex-1 flex-col">
            <AccountScopeBoundary>{children}</AccountScopeBoundary>
          </div>
        ) : (
          <AccountScopeBoundary>{children}</AccountScopeBoundary>
        )}
      </main>

      {showSubscriptionBanner && (
        <div className="fixed top-0 left-0 right-0 z-[70]">
          <TrialBanner
            daysRemaining={daysRemaining}
            isExpired={!!isSubscriptionExpired || !!hasNoPlan}
            isTrialPlan={isTrialPlan}
            hasNoPlan={!!hasNoPlan}
            onDismiss={() => setBannerDismissed(true)}
          />
        </div>
      )}


      {globalOverlays}

    </div>
  );
};

export const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const getInitialSidebarState = () => {
    if (typeof window === 'undefined') return true;
    const pathname = window.location.pathname;
    const isAdminLibraryBuilder = pathname === '/admin/library/flow/builder' || pathname === '/admin/library/whatsapp-flow/builder';
    const isFlowBuilderPage = isAdminLibraryBuilder || (pathname.startsWith('/home/automation/flows/') &&
      (pathname.includes('/new') || pathname.match(/\/home\/automation\/flows\/[^/]+$/))) ||
      isSocialFlowBuilderPath(pathname);
    const isAdminRoute = pathname.startsWith('/admin');
    const isAgencyRoute = pathname.startsWith('/home/agency');
    if ((isAdminRoute && !isAdminLibraryBuilder) || isAgencyRoute) return true;
    return !isFlowBuilderPage; // Collapsed on FlowBuilder pages, expanded elsewhere
  };

  return (
    <SidebarProvider
      defaultOpen={getInitialSidebarState()}
      style={{ ['--sidebar-width' as any]: '244px', ['--sidebar-width-icon' as any]: '72px' }}
    >
      <GenUIFullProvider>
        <AiChatProvider>
          <GenUIProvider>
            <AdminAnnouncementModal />
            <InAppAnnouncementHost />
            <PortalCampaignHost />
            <AppShellContent>{children}</AppShellContent>
          </GenUIProvider>
        </AiChatProvider>
      </GenUIFullProvider>
    </SidebarProvider>
  );
};
